// Story 8.10 (ADO-2019): the HHT pages (DV-34).
//
//   /hht-orders        two or more orders waiting: the "Waiting orders" list, oldest first;
//                      exactly one: straight to its review (a replace, so Back skips the
//                      list); none: "Waiting orders" with the "No HHT orders waiting" card and
//                      no count pill (Q5). Plus the checking, failed and not-applicable states.
//   /hht-orders/{id}   the review of that one order (DV-32). With two or more waiting it
//                      shows "← HHT Orders" and "Order N of M · Scanned {date}" with "Next
//                      order", which wraps from the last to the first; with one, "← Select
//                      Depot" and neither. An order already accepted shows "Already added"
//                      (Q4: who added it and when); an id whose review answers 404 goes back
//                      to /hht-orders with no notice.
//   After "Add to carts" the result shows on the same URL (page state, no route): what was
//   added, the carts that received a line and, under them, the lines not added that the
//   review had not already disclosed (Q3); an accept that added nothing shows the "Order
//   closed" panel (Q6). With other orders still waiting, "Order N of M added (closed,
//   Q6) · K more waiting" and a primary "Review next order" (the next waiting order after
//   the accepted one, wrapping); otherwise only "Back to Select Depot". The header badge
//   counts down at once and both reads are taken fresh (`markAccepted` on the waiting count
//   and on the details, DV-39). The ordering choices are re-read in the background, so the
//   depot switcher's rows count what the accept added (Story 8.1, ADO-2010, A9.1).
// The HHT history sits below every screen when there is any (DV-8, DV-55). No depot is
// needed: an order is split across the Customer's depot carts by the server (DV-1). The bar
// is the chooser header shared with /choose (DV-37).
//
// The details are the shared store (`useHhtOrdersStore`), re-read on entry; their loading,
// failure and Retry are this page's. The header badge reads the waiting count store
// (`useHhtWaitingCountStore`, DV-39). The review is read here. Accepting names the reviewed
// order and the shown substitutes the Shopper kept (DV-4, DV-18). An accept that lost the race
// (the typed 409, D-12, nothing was written) re-reads the order, which then answers who
// accepted it (Q4); a 404 returns to /hht-orders with no notice. Any other failure shows the
// "Not added to carts" banner with a Refresh. An accept with nothing that can be added still
// succeeds (DV-23): the result then lists no cart.
import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react';
import { Link, Navigate, useLocation, useNavigate, useParams } from 'react-router';
import { Banner, Button, Icon } from '../../design-system';
import { sameCartTarget } from '../../api/cart-target';
import { useSessionStore } from '../../auth/useSessionStore';
import { sessionCartTarget } from '../../basket/sessionCart';
import { useBasketStore } from '../../basket/useBasketStore';
import {
  acceptHhtOrders,
  HHT_ORDER_NOT_WAITING_PROBLEM_TYPE,
  HhtOrderProblemError,
  keptSubstitutesFrom,
  type HhtAcceptanceResult,
  type HhtWaitingOrder,
} from '../../api/hht-orders.client';
import { useHhtOrdersStore } from '../../hht/useHhtOrdersStore';
import { useHhtWaitingCountStore } from '../../hht/useHhtWaitingCountStore';
import { hhtProblemMessage, useHhtReview } from '../../hht/useHhtReview';
import { CHOOSE_ROUTE } from '../../ordering/orderingChoices';
import { reloadOrderingChoices } from '../../ordering/orderingSession';
import { formatWeekdayDateTime } from '../../shared/datetime';
import { ChooserHeader } from '../Choose/ChooserHeader';
import { HhtAddedToCarts } from './HhtAddedToCarts';
import { HhtAlreadyAdded } from './HhtAlreadyAdded';
import { HhtHistory } from './HhtHistory';
import { HhtNotAddedList } from './HhtNotAddedList';
import { hhtNotAddedCards, type HhtNotAddedCard } from './hhtNotAdded';
import { HhtPill } from './HhtPill';
import { HhtReview } from './HhtReview';
import { HhtNoOrdersWaiting, HhtWaitingList } from './HhtWaitingList';
import { hhtOrderPath, HHT_ORDERS_ROUTE } from './hhtRoutes';
import styles from './HhtOrders.module.css';

/** DV-34: the list's subtitle. */
export const HHT_LIST_SUBTITLE =
  'Review each order and add it to your depot carts. Orders are not sent until you submit each cart.';

const GENERIC_ACCEPT_ERROR = 'The order could not be added to your carts. Please try again.';

/** What a review screen hands the list when it sends the Shopper back to it: the order
 *  that was not waiting, never redirected to again from this visit. No text is shown. */
interface HhtListReturn {
  readonly notWaitingId: string;
}

function returnFrom(state: unknown): HhtListReturn | null {
  if (typeof state !== 'object' || state === null) return null;
  const { notWaitingId } = state as Partial<Record<keyof HhtListReturn, unknown>>;
  return typeof notWaitingId === 'string' ? { notWaitingId } : null;
}

/** Another accept won the race for the order: the typed 409 (D-12), nothing was written. */
function isAlreadyAccepted(error: unknown): boolean {
  return (
    error instanceof HhtOrderProblemError &&
    error.status === 409 &&
    error.problemType === HHT_ORDER_NOT_WAITING_PROBLEM_TYPE
  );
}

/** The order is gone: a 404 (DV-32). */
function isNotFound(error: unknown): boolean {
  return error instanceof HhtOrderProblemError && error.status === 404;
}

/** An accept's result, with where the order stood among those waiting when it was added. */
interface AcceptedOrder {
  readonly hhtOrderId: string;
  readonly result: HhtAcceptanceResult;
  /** Q3: what was not added that the review had not already shown as not added. */
  readonly notAdded: readonly HhtNotAddedCard[];
  /** 1-based position among the waiting orders, or 0 when the overview did not list it. */
  readonly position: number;
  readonly total: number;
  /** The waiting order ids at accept time, oldest first: "Review next order" follows it. */
  readonly rotation: readonly string[];
}

/** The order after `afterId` in `rotation` that still waits, wrapping; else the oldest
 *  order still waiting (one that arrived since). Null when nothing waits. */
function nextWaitingId(
  rotation: readonly string[],
  afterId: string,
  stillWaiting: readonly HhtWaitingOrder[],
): string | null {
  const waitingIds = new Set(stillWaiting.map((order) => order.hhtOrderId));
  const start = rotation.indexOf(afterId);
  for (let step = 1; step <= rotation.length; step += 1) {
    const candidate = rotation[(start + step) % rotation.length];
    if (candidate !== undefined && waitingIds.has(candidate)) return candidate;
  }
  return stillWaiting[0]?.hhtOrderId ?? null;
}

function BackLink({ to, children }: { readonly to: string; readonly children: ReactNode }) {
  return (
    <Link to={to} className={styles.backLink}>
      <Icon name="arrowLeft" size={14} />
      {children}
    </Link>
  );
}

/** The route element: one screen per order id, so its state (the removed substitutes, an
 *  accept's result) never carries over to the next order. */
export function HhtOrdersPage() {
  const { hhtOrderId } = useParams();
  return <HhtOrdersScreen key={hhtOrderId ?? ''} hhtOrderId={hhtOrderId ?? null} />;
}

function HhtOrdersScreen({ hhtOrderId }: { readonly hhtOrderId: string | null }) {
  const navigate = useNavigate();
  const location = useLocation();
  const storeStatus = useHhtOrdersStore((s) => s.status);
  const applies = useHhtOrdersStore((s) => s.applies);
  const waiting = useHhtOrdersStore((s) => s.waiting);
  const history = useHhtOrdersStore((s) => s.history);
  const historyTotal = useHhtOrdersStore((s) => s.historyTotal);
  const load = useHhtOrdersStore((s) => s.load);
  const markAccepted = useHhtOrdersStore((s) => s.markAccepted);
  const markCountAccepted = useHhtWaitingCountStore((s) => s.markAccepted);

  const [refreshed, setRefreshed] = useState(false);
  const [accepted, setAccepted] = useState<AcceptedOrder | null>(null);
  const [accepting, setAccepting] = useState(false);
  // Set synchronously, so a second click that lands before the "accepting" re-render cannot
  // post the accept again (D-12 makes it safe, but it would answer the lost-race screen).
  const acceptInFlight = useRef(false);
  const [acceptError, setAcceptError] = useState<string | null>(null);
  const [removedSubstitutes, setRemovedSubstitutes] = useState<ReadonlySet<string>>(
    () => new Set(),
  );
  const [reloadToken, setReloadToken] = useState(0);
  // Orders added from this page, kept out of "waiting" even if the re-read that follows
  // the accept fails and the store still lists them.
  const [acceptedIds, setAcceptedIds] = useState<ReadonlySet<string>>(() => new Set());
  const stillWaiting = waiting.filter((order) => !acceptedIds.has(order.hhtOrderId));

  // Re-read on entry, so a scan uploaded since sign-in shows here. Where to go next (the
  // one order, or back from an order no longer waiting) is decided on this fresh read.
  useEffect(() => {
    let live = true;
    void load().then(() => {
      if (live) setRefreshed(true);
    });
    return () => {
      live = false;
    };
  }, [load]);

  const review = useHhtReview(accepted === null ? hhtOrderId : null, reloadToken);
  const listReturn = returnFrom(location.state);

  // One-way: the page offers no undo (DV-49); another order starts afresh (the key above).
  const removeSubstitute = useCallback((skuCode: string) => {
    setRemovedSubstitutes((current) => new Set([...current, skuCode]));
  }, []);

  const refresh = useCallback(() => {
    setAcceptError(null);
    setReloadToken((token) => token + 1);
    void load();
  }, [load]);

  const position =
    hhtOrderId === null ? -1 : stillWaiting.findIndex((o) => o.hhtOrderId === hhtOrderId);
  const current = position === -1 ? undefined : stillWaiting[position];

  // Once the result is on screen, drop the accepted order from the badge and re-read fresh
  // (never a read begun before). After the commit, so this screen never sees its order gone
  // from "waiting" without the result that explains it.
  useEffect(() => {
    if (accepted === null) return;
    void markAccepted([accepted.hhtOrderId]);
    // Keyed on the order id, so a second run of this effect cannot count it off twice.
    void markCountAccepted([accepted.hhtOrderId]);
  }, [accepted, markAccepted, markCountAccepted]);

  const accept = useCallback(async () => {
    if (review?.status !== 'ready' || accepting || acceptInFlight.current) return;
    acceptInFlight.current = true;
    const reviewedId = review.review.hhtOrderId;
    setAccepting(true);
    setAcceptError(null);
    try {
      // Story 8.3 (ADO-2012), D-23: the depot cart lines the review was read against go
      // back with the accept, so a line that changed meanwhile is not added (card 08).
      const result = await acceptHhtOrders(
        [reviewedId],
        keptSubstitutesFrom(review.review, removedSubstitutes),
        review.review.cartLines,
      );
      setAcceptedIds((ids) => new Set([...ids, reviewedId]));
      setAccepted({
        hhtOrderId: reviewedId,
        result,
        notAdded: hhtNotAddedCards(result.notAdded, review.review, removedSubstitutes),
        position: position + 1,
        total: stillWaiting.length,
        rotation: stillWaiting.map((order) => order.hhtOrderId),
      });
      // The side panel's cart is the session's: re-read it if this order added to it. A cart is
      // its depot, account and entry kind (Story 8.3 D-1; Enabler E8-G DA-8), and the accept names
      // each cart's account (8.10 phase 4).
      const sessionCart = sessionCartTarget();
      if (
        sessionCart !== null &&
        result.carts.some((cart) =>
          sameCartTarget(sessionCart, {
            depotId: cart.depotId,
            customerAccountCode: cart.customerAccountCode,
            entryKind: cart.entryKind ?? 'DEPOT',
          }),
        )
      ) {
        void useBasketStore.getState().hydrate(sessionCart);
      }
      // The switcher's rows count the choices' open carts, which the accept just wrote to: once
      // they have been read, re-read them in the background (no loading state, and a failed read
      // keeps the last good one). The read waits for the cart writes still in flight, so it cannot
      // reach the server before them. It is the session's, so it lands after this page has gone.
      if (useSessionStore.getState().orderingChoices !== null) {
        void useBasketStore
          .getState()
          .writesSettled()
          .then(() => reloadOrderingChoices({ background: true }));
      }
    } catch (error) {
      if (isAlreadyAccepted(error)) {
        // Nothing was added again. Re-read the order, which now answers who accepted it and
        // when (Q4), and the badge's count and the details (DV-39).
        void useHhtWaitingCountStore.getState().load();
        void load();
        setReloadToken((token) => token + 1);
        return;
      }
      if (isNotFound(error)) {
        void useHhtWaitingCountStore.getState().load();
        const state: HhtListReturn = { notWaitingId: reviewedId };
        navigate(HHT_ORDERS_ROUTE, { replace: true, state });
        return;
      }
      setAcceptError(hhtProblemMessage(error, GENERIC_ACCEPT_ERROR));
    } finally {
      acceptInFlight.current = false;
      setAccepting(false);
    }
  }, [review, accepting, removedSubstitutes, position, stillWaiting, navigate, load]);

  const backToList = (id: string) => (
    <Navigate to={HHT_ORDERS_ROUTE} replace state={{ notWaitingId: id } satisfies HhtListReturn} />
  );

  const overviewFailed = storeStatus === 'error' && !applies;
  const notAvailable = (
    <div className={styles.head}>
      <h1 className={styles.title}>HHT Orders</h1>
      <p className={styles.subtitle} role="status">
        Handheld terminal orders are not available for this account.
      </p>
    </div>
  );
  const overviewError = (
    <Banner type="error" title="We couldn't check for handheld orders">
      Please check your connection and try again.{' '}
      <Button variant="secondary" onClick={refresh}>
        Try again
      </Button>
    </Banner>
  );
  // Q5: no count pill when nothing waits.
  const listHead = (count: number) => (
    <div className={styles.head}>
      <div className={styles.titleRow}>
        <h1 className={styles.title}>HHT Orders</h1>
        {count > 0 ? <HhtPill tone="warning">{`${count} waiting`}</HhtPill> : null}
      </div>
      <p className={styles.subtitle}>{HHT_LIST_SUBTITLE}</p>
    </div>
  );
  const checking = (message: string) => (
    <p className={styles.status} role="status">
      {message}
    </p>
  );

  let back: ReactNode = <BackLink to={CHOOSE_ROUTE}>Select Depot</BackLink>;
  let body: ReactNode;

  if (hhtOrderId === null) {
    // /hht-orders
    const severalKnown = applies && stillWaiting.length >= 2;
    const [only] = stillWaiting;
    if (!refreshed && !severalKnown) {
      body = checking('Checking for handheld orders…');
    } else if (overviewFailed) {
      body = overviewError;
    } else if (!applies) {
      body = notAvailable;
    } else if (only === undefined) {
      // Q5: "Waiting orders" with the "No HHT orders waiting" card.
      body = (
        <>
          {listHead(0)}
          <HhtNoOrdersWaiting />
        </>
      );
    } else if (stillWaiting.length === 1 && only.hhtOrderId !== listReturn?.notWaitingId) {
      return <Navigate to={hhtOrderPath(only.hhtOrderId)} replace />;
    } else {
      body = (
        <>
          {listHead(stillWaiting.length)}
          <HhtWaitingList waiting={stillWaiting} />
        </>
      );
    }
  } else if (accepted !== null) {
    // The result, on the same URL.
    const nextId = nextWaitingId(accepted.rotation, hhtOrderId, stillWaiting);
    // Q6: an accept that added nothing closed the order.
    const closed = accepted.result.added.lineCount === 0;
    const outcome = closed ? 'closed' : 'added';
    const added =
      accepted.position > 0
        ? `Order ${accepted.position} of ${accepted.total} ${outcome}`
        : `Order ${outcome}`;
    body = (
      <>
        <div className={styles.head}>
          {stillWaiting.length > 0 ? (
            <p className={styles.orderPosition}>
              {`${added} · ${stillWaiting.length} more waiting`}
            </p>
          ) : null}
          <h1 className={styles.title}>HHT Orders</h1>
          <p className={styles.subtitle}>
            {closed
              ? 'This HHT order has been reviewed and closed.'
              : 'Lines are in your depot carts — open one to review and send.'}
          </p>
        </div>
        <HhtAddedToCarts
          result={accepted.result}
          notAddedCount={accepted.notAdded.length}
          nextOrderPath={nextId === null ? null : hhtOrderPath(nextId)}
        />
        <HhtNotAddedList cards={accepted.notAdded} />
      </>
    );
  } else if (review?.status === 'notFound') {
    return backToList(hhtOrderId);
  } else if (review?.status === 'accepted') {
    // Q4: the order was added already (by a colleague, or by a lost accept on this page).
    body = <HhtAlreadyAdded accepted={review.accepted} nowMs={Date.now()} />;
  } else if (refreshed && storeStatus === 'ready' && !applies) {
    body = notAvailable;
  } else {
    const several = stillWaiting.length >= 2 && current !== undefined;
    if (several) back = <BackLink to={HHT_ORDERS_ROUTE}>HHT Orders</BackLink>;
    const next = several ? stillWaiting[(position + 1) % stillWaiting.length] : undefined;
    const orderNav =
      several && current !== undefined ? (
        <div className={styles.orderNav}>
          <p className={styles.orderPosition}>
            {`Order ${position + 1} of ${stillWaiting.length} · Scanned ${formatWeekdayDateTime(current.receivedAt)}`}
          </p>
          {next !== undefined ? (
            <Link to={hhtOrderPath(next.hhtOrderId)} className={styles.nextOrder}>
              Next order
              <Icon name="arrowRight" size={14} />
            </Link>
          ) : null}
        </div>
      ) : null;
    if (review === null || review.status === 'loading') {
      body = checking('Loading your handheld order…');
    } else if (review.status === 'error') {
      body = (
        <Banner type="error" title="We couldn't load your handheld order">
          {review.message}{' '}
          <Button variant="secondary" onClick={refresh}>
            Refresh
          </Button>
        </Banner>
      );
    } else {
      body = (
        <>
          {orderNav}
          <HhtReview
            review={review.review}
            removedSubstitutes={removedSubstitutes}
            onRemoveSubstitute={removeSubstitute}
            onAccept={() => void accept()}
            accepting={accepting}
            acceptError={
              acceptError !== null ? (
                <Banner type="error" title="Not added to carts">
                  {acceptError}{' '}
                  <Button variant="secondary" onClick={refresh}>
                    Refresh
                  </Button>
                </Banner>
              ) : null
            }
          />
        </>
      );
    }
  }

  return (
    <div className={styles.page}>
      <ChooserHeader />
      <main className={styles.main}>
        {back}
        {body}
        {applies && history.length > 0 ? (
          <HhtHistory history={history} total={historyTotal} />
        ) : null}
      </main>
    </div>
  );
}
